import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { CompositeChart } from "../composite-chart";
import { CompositionMathPanel } from "../composition-math-tooltip";
import { CreateCompositePanel } from "../create-composite-panel";
import { PerpChartView, PERP_COLOR } from "../perp-chart-view";
import { PerpsPanel, type PerpPanelMode } from "../perps-panel";
import { PredictionChartView, PRED_COLOR } from "../prediction-chart-view";
import {
  PredictionsPanel,
  type PredPanelMode,
} from "../predictions-panel";
import { RiskRewardTiles } from "../risk-reward-tiles";
import { ScenariosTable } from "../scenarios-table";
import { TopBar } from "../top-bar";
import { MobileSheet } from "./mobile-sheet";
import { MobilePerpCard, MobilePredCard } from "./mobile-leg-card";
import type {
  PayoffPoint,
  PerpLeg,
  PredictionLeg,
} from "../../lib/composePayoff";
import type { Scenario } from "../../lib/scenarios";
import type { ExecutionState } from "../../hooks/use-composite-execution";
import type { SelectedPerp, SelectedPred } from "../../types";

const GOLD = "#f5a623";
const BRAND = "#9ecbff";

const mono10: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  fontWeight: 600,
};

const sectionTitle: CSSProperties = {
  ...mono10,
  fontSize: 9,
  color: "var(--fg-dim)",
  padding: "0 14px",
  marginBottom: 8,
};

// ============================================================================
// Props
// ============================================================================

export type MobileAppProps = {
  // Markets
  selectedPerp: SelectedPerp | null;
  selectedPred: SelectedPred | null;
  onOpenPerpsModal: () => void;
  onOpenPredsModal: () => void;

  // Legs
  perpLeg: PerpLeg | null;
  predLeg: PredictionLeg | null;
  perpMode: PerpPanelMode;
  predMode: PredPanelMode;
  setPerpMode: Dispatch<SetStateAction<PerpPanelMode>>;
  setPredMode: Dispatch<SetStateAction<PredPanelMode>>;
  setPerpLeg: (leg: PerpLeg) => void;
  setPredLeg: (leg: PredictionLeg) => void;
  initPerpLeg: () => void;
  initPredLeg: () => void;

  // Composite
  payoff: ReadonlyArray<PayoffPoint> | null;
  derived: {
    mark: number;
    atMark: number;
    maxGain: number;
    maxLoss: number;
    breakevens: number[];
    scenarios: Scenario[];
    strategy: string;
  } | null;
  activeScenario: Scenario | null;
  setHoverScenario: (s: Scenario | null) => void;
  setPinnedScenario: Dispatch<SetStateAction<Scenario | null>>;

  // Sheets / panels
  mathOpen: boolean;
  setMathOpen: Dispatch<SetStateAction<boolean>>;
  createOpen: boolean;
  setCreateOpen: Dispatch<SetStateAction<boolean>>;

  // Execution
  executionState: ExecutionState;
  walletReady: boolean;
  onConfirmExecute: () => void;

  // Top bar
  onSimulate: () => void;
};

// ============================================================================
// Root
// ============================================================================

export function MobileApp(props: MobileAppProps) {
  const [perpSheetOpen, setPerpSheetOpen] = useState(false);
  const [predSheetOpen, setPredSheetOpen] = useState(false);

  const perpReady = !!props.selectedPerp && !!props.perpLeg;
  const predReady = !!props.selectedPred && !!props.predLeg;
  const compositeReady = !!props.derived;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        background: "var(--bg-0)",
        color: "var(--fg-hi)",
      }}
    >
      <TopBar compact onSimulate={props.onSimulate} />

      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          paddingTop: 14,
          // leave room for the sticky CTA bar (or its absence)
          paddingBottom: compositeReady ? 88 : 16,
        }}
      >
        {/* ── Step 1: Leg cards ────────────────────────────────────────── */}
        <section>
          <div style={sectionTitle}>YOUR LEGS</div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "0 12px",
            }}
          >
            <MobilePerpCard
              market={props.selectedPerp}
              leg={props.perpLeg}
              color={PERP_COLOR}
              onTap={() => setPerpSheetOpen(true)}
              onSelectMarket={props.onOpenPerpsModal}
            />
            <MobilePredCard
              market={props.selectedPred}
              leg={props.predLeg}
              color={PRED_COLOR}
              onTap={() => setPredSheetOpen(true)}
              onSelectMarket={props.onOpenPredsModal}
            />
          </div>
        </section>

        {/* ── Step 2: Composite ────────────────────────────────────────── */}
        <section>
          <div
            style={{
              ...sectionTitle,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>Composite</span>
            <ProgressDots
              perpReady={perpReady}
              predReady={predReady}
              composite={compositeReady}
            />
          </div>
          <CompositeSection
            payoff={props.payoff}
            derived={props.derived}
            perpLeg={props.perpLeg}
            predLeg={props.predLeg}
            selectedPerp={props.selectedPerp}
            selectedPred={props.selectedPred}
            activeScenario={props.activeScenario}
            onMath={() => props.setMathOpen(true)}
          />
        </section>

        {/* ── Step 3: Risk / Reward + Scenarios ────────────────────────── */}
        {props.derived && (
          <>
            <section>
              <div style={sectionTitle}>Risk / Reward</div>
              <div style={{ padding: "0 12px" }}>
                <div
                  style={{
                    border: "1px solid var(--line-2)",
                    borderRadius: 4,
                    background: "var(--bg-1)",
                    overflow: "hidden",
                  }}
                >
                  <RiskRewardTiles
                    maxGain={props.derived.maxGain}
                    maxLoss={props.derived.maxLoss}
                    atMark={props.derived.atMark}
                    breakevens={props.derived.breakevens}
                    strategy={props.derived.strategy}
                  />
                </div>
              </div>
            </section>

            <section>
              <div style={sectionTitle}>Scenarios · P&L at key prices</div>
              <div
                style={{
                  margin: "0 12px",
                  border: "1px solid var(--line-2)",
                  borderRadius: 4,
                  background: "var(--bg-1)",
                  overflow: "hidden",
                  // The table renders with its own internal scroll; clamp the
                  // outer height so the page can keep scrolling past it.
                  maxHeight: 360,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <ScenariosTable
                  rows={props.derived.scenarios}
                  activePrice={props.activeScenario?.price ?? null}
                  onHover={props.setHoverScenario}
                  onSelect={(s) =>
                    props.setPinnedScenario((prev) =>
                      prev && prev.price === s.price ? null : s,
                    )
                  }
                />
              </div>
            </section>
          </>
        )}
      </main>

      {/* Sticky bottom action bar — only after composite is computed */}
      {compositeReady && (
        <BottomActionBar
          onMath={() => props.setMathOpen(true)}
          onCreate={() => props.setCreateOpen(true)}
          createBusy={
            props.executionState.stage !== "idle" &&
            props.executionState.stage !== "success" &&
            props.executionState.stage !== "error"
          }
        />
      )}

      {/* ─────────────── Bottom sheets ─────────────── */}

      <MobileSheet
        open={perpSheetOpen}
        onClose={() => setPerpSheetOpen(false)}
        title={props.selectedPerp?.coin ?? "PERP"}
        subtitle="HYPERLIQUID"
      >
        <PerpConfigSheetBody
          selectedPerp={props.selectedPerp}
          perpLeg={props.perpLeg}
          perpMode={props.perpMode}
          setPerpMode={props.setPerpMode}
          setPerpLeg={props.setPerpLeg}
          initPerpLeg={props.initPerpLeg}
          onExplore={props.onOpenPerpsModal}
        />
      </MobileSheet>

      <MobileSheet
        open={predSheetOpen}
        onClose={() => setPredSheetOpen(false)}
        title="PREDICTION"
        subtitle="POLYMARKET"
      >
        <PredConfigSheetBody
          selectedPred={props.selectedPred}
          predLeg={props.predLeg}
          predMode={props.predMode}
          setPredMode={props.setPredMode}
          setPredLeg={props.setPredLeg}
          initPredLeg={props.initPredLeg}
          selectedPerp={props.selectedPerp}
          onExplore={props.onOpenPredsModal}
        />
      </MobileSheet>

      <MobileSheet
        open={props.mathOpen}
        onClose={() => props.setMathOpen(false)}
        title="HOW IT WORKS"
        subtitle="COMPOSITE MATH"
      >
        {props.derived &&
        props.perpLeg &&
        props.predLeg &&
        props.selectedPerp &&
        props.payoff ? (
          <CompositionMathPanel
            perp={props.perpLeg}
            pred={props.predLeg}
            perpSymbol={props.selectedPerp.coin}
            mark={props.derived.mark}
            payoff={props.payoff}
            onClose={() => props.setMathOpen(false)}
          />
        ) : null}
      </MobileSheet>

      <MobileSheet
        open={props.createOpen}
        onClose={() => props.setCreateOpen(false)}
        title="REVIEW & CREATE"
        subtitle="NEW COMPOSITE"
        height="92vh"
      >
        {props.derived &&
        props.perpLeg &&
        props.predLeg &&
        props.selectedPerp &&
        props.selectedPred ? (
          <CreateCompositePanel
            perp={props.perpLeg}
            pred={props.predLeg}
            perpMarket={props.selectedPerp}
            predMarket={props.selectedPred}
            mark={props.derived.mark}
            maxGain={props.derived.maxGain}
            maxLoss={props.derived.maxLoss}
            onClose={() => props.setCreateOpen(false)}
            onConfirm={props.onConfirmExecute}
            executionState={props.executionState}
            walletReady={props.walletReady}
          />
        ) : null}
      </MobileSheet>
    </div>
  );
}

// ============================================================================
// Composite section (chart + math button + empty state)
// ============================================================================

function CompositeSection({
  payoff,
  derived,
  perpLeg,
  predLeg,
  selectedPerp,
  selectedPred,
  activeScenario,
  onMath,
}: {
  payoff: ReadonlyArray<PayoffPoint> | null;
  derived: MobileAppProps["derived"];
  perpLeg: PerpLeg | null;
  predLeg: PredictionLeg | null;
  selectedPerp: SelectedPerp | null;
  selectedPred: SelectedPred | null;
  activeScenario: Scenario | null;
  onMath: () => void;
}) {
  if (!derived || !payoff || !perpLeg || !predLeg) {
    const needPerp = !selectedPerp;
    const needPred = !selectedPred;
    const needsSim = !needPerp && !needPred && !derived;
    return (
      <div
        style={{
          margin: "0 12px",
          padding: "32px 18px",
          borderRadius: 4,
          border: "1px dashed var(--line-3)",
          background: "var(--bg-1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          textAlign: "center",
        }}
      >
        <div style={{ ...mono10, fontSize: 9, color: "var(--fg-dim)" }}>
          COMPOSITE PAYOFF
        </div>
        <div
          style={{
            fontFamily: "var(--f-sans)",
            fontSize: 13,
            color: "var(--fg-meta)",
            lineHeight: 1.4,
          }}
        >
          {needPerp && needPred
            ? "Pick a Perp + a Prediction to compose."
            : needPerp
              ? "Pick a Perp leg to compose."
              : needPred
                ? "Pick a Prediction leg to compose."
                : needsSim
                  ? "Configure both legs to see the payoff."
                  : "Configure both legs to compose."}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        margin: "0 12px",
        position: "relative",
        background: `
          linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 8%),
          linear-gradient(180deg, var(--bg-2) 0%, var(--bg-1) 60%, var(--bg-1) 100%)
        `,
        border: `1px solid rgba(255,255,255,0.08)`,
        borderRadius: 4,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 0 rgba(0,0,0,0.5)`,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onMath}
        aria-label="How it works"
        style={{
          position: "absolute",
          top: 8,
          left: 8,
          width: 24,
          height: 24,
          borderRadius: 12,
          background: "rgba(0,0,0,0.45)",
          border: "1px solid var(--fg-max)",
          color: "var(--fg-max)",
          cursor: "pointer",
          fontFamily: "var(--f-mono)",
          fontSize: 12,
          fontWeight: 500,
          display: "grid",
          placeItems: "center",
          padding: 0,
          lineHeight: 1,
          zIndex: 2,
        }}
      >
        ?
      </button>
      <div style={{ height: 280, padding: 8 }}>
        <CompositeChart
          points={payoff}
          mark={derived.mark}
          threshold={predLeg.threshold}
          liquidation={perpLeg.liquidation}
          highlight={
            activeScenario
              ? { price: activeScenario.price, label: activeScenario.label }
              : null
          }
        />
      </div>
    </div>
  );
}

// ============================================================================
// Sub-views
// ============================================================================

function ProgressDots({
  perpReady,
  predReady,
  composite,
}: {
  perpReady: boolean;
  predReady: boolean;
  composite: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <Dot active={perpReady} color={PERP_COLOR} />
      <Dot active={predReady} color={PRED_COLOR} />
      <Dot active={composite} color={GOLD} />
    </div>
  );
}

function Dot({ active, color }: { active: boolean; color: string }) {
  return (
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: active ? color : "transparent",
        border: `1px solid ${active ? color : "var(--line-3)"}`,
        boxShadow: active ? `0 0 6px ${color}88` : "none",
        transition: "background 200ms ease, box-shadow 200ms ease",
      }}
    />
  );
}

function BottomActionBar({
  onMath,
  onCreate,
  createBusy,
}: {
  onMath: () => void;
  onCreate: () => void;
  createBusy: boolean;
}) {
  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        padding: "10px 12px calc(env(safe-area-inset-bottom, 0) + 10px)",
        background:
          "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0.95) 100%)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderTop: "1px solid var(--line-2)",
        display: "flex",
        gap: 8,
      }}
    >
      <button
        type="button"
        onClick={onMath}
        style={{
          flex: "0 0 auto",
          padding: "11px 14px",
          borderRadius: 4,
          background: "transparent",
          border: "1px solid var(--line-3)",
          color: "var(--fg-max)",
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        ? Math
      </button>
      <button
        type="button"
        onClick={onCreate}
        disabled={createBusy}
        style={{
          flex: 1,
          padding: "11px 14px",
          borderRadius: 4,
          background: createBusy
            ? "rgba(158,203,255,0.18)"
            : `linear-gradient(180deg, ${BRAND} 0%, #7ab3ee 100%)`,
          border: `1px solid ${BRAND}`,
          color: "var(--bg-0)",
          fontFamily: "var(--f-mono)",
          fontSize: 12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 700,
          cursor: createBusy ? "not-allowed" : "pointer",
          boxShadow: createBusy
            ? "none"
            : `0 0 18px ${BRAND}33, inset 0 1px 0 rgba(255,255,255,0.3)`,
        }}
      >
        {createBusy ? "Executing…" : "Create Composite"}
      </button>
    </div>
  );
}

// ============================================================================
// Sheet bodies — wrap the existing chart + panel components
// ============================================================================

function PerpConfigSheetBody({
  selectedPerp,
  perpLeg,
  perpMode,
  setPerpMode,
  setPerpLeg,
  initPerpLeg,
  onExplore,
}: {
  selectedPerp: SelectedPerp | null;
  perpLeg: PerpLeg | null;
  perpMode: PerpPanelMode;
  setPerpMode: Dispatch<SetStateAction<PerpPanelMode>>;
  setPerpLeg: (leg: PerpLeg) => void;
  initPerpLeg: () => void;
  onExplore: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        style={{
          height: 200,
          flexShrink: 0,
          borderBottom: "1px solid var(--line-2)",
        }}
      >
        <PerpChartView selected={selectedPerp} leg={perpLeg} />
      </div>
      <PerpsPanel
        selected={selectedPerp}
        leg={perpLeg}
        mode={perpMode}
        onModeChange={setPerpMode}
        onLegChange={setPerpLeg}
        onSimulateInit={initPerpLeg}
        onExplore={onExplore}
      />
    </div>
  );
}

function PredConfigSheetBody({
  selectedPred,
  predLeg,
  predMode,
  setPredMode,
  setPredLeg,
  initPredLeg,
  selectedPerp,
  onExplore,
}: {
  selectedPred: SelectedPred | null;
  predLeg: PredictionLeg | null;
  predMode: PredPanelMode;
  setPredMode: Dispatch<SetStateAction<PredPanelMode>>;
  setPredLeg: (leg: PredictionLeg) => void;
  initPredLeg: () => void;
  selectedPerp: SelectedPerp | null;
  onExplore: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        style={{
          height: 200,
          flexShrink: 0,
          borderBottom: "1px solid var(--line-2)",
        }}
      >
        <PredictionChartView selected={selectedPred} leg={predLeg} />
      </div>
      <PredictionsPanel
        selected={selectedPred}
        leg={predLeg}
        mode={predMode}
        perp={selectedPerp}
        perpSymbol={selectedPerp?.coin}
        onModeChange={setPredMode}
        onLegChange={setPredLeg}
        onSimulateInit={initPredLeg}
        onExplore={onExplore}
      />
    </div>
  );
}
